import { Box, Divider, FormControl, FormControlLabel, InputLabel, MenuItem, Radio, RadioGroup, Select, Slider, TextField, Typography, useMediaQuery, useTheme } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { ChangeEvent, Dispatch, FC, SetStateAction, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { IBeehiveStats } from "../../../../../common/interfaces/beehiveStats";
import { ICustomOption } from "../../../../../common/interfaces/form";

const DialogStat: FC<{
    currentBeehiveStat?: IBeehiveStats,
    setBeehiveStatBody: Dispatch<SetStateAction<Partial<IBeehiveStats> | undefined>>
}> = (
    { currentBeehiveStat, setBeehiveStatBody }
) => {
        const { t } = useTranslation();
        const theme = useTheme();
        const largeMediumScreen = useMediaQuery(theme.breakpoints.up("sm"));

        const pastureOptions: ICustomOption[] = [
            {
                value: "dandelion",
                desc: t("beehiveDetailsPage.tabs.statsForm.dandelion")
            },
            {
                value: "acacia",
                desc: t("beehiveDetailsPage.tabs.statsForm.acacia")
            },
            {
                value: "limeTree",
                desc: t("beehiveDetailsPage.tabs.statsForm.limeTree")
            },
            {
                value: "chestnut",
                desc: t("beehiveDetailsPage.tabs.statsForm.chestnut")
            },
            {
                value: "honeydrew",
                desc: t("beehiveDetailsPage.tabs.statsForm.honeydrew")
            },
            {
                value: "other",
                desc: t("beehiveDetailsPage.tabs.statsForm.other")
            },
        ]

        const varroaTreatmentOptions: ICustomOption[] = [
            {
                value: "broodingBlock",
                desc: t("beehiveDetailsPage.tabs.statsForm.broodingBlock")
            },
            {
                value: "oxalicAcid",
                desc: t("beehiveDetailsPage.tabs.statsForm.oxalicAcid")
            },
            {
                value: "other",
                desc: t("beehiveDetailsPage.tabs.statsForm.other")
            },
        ]

        const otherSanitaryProblemsOptions: ICustomOption[] = [
            {
                value: "calcifiedBrood",
                desc: t("beehiveDetailsPage.tabs.statsForm.calcifiedBrood")
            },
            {
                value: "europeanPlague",
                desc: t("beehiveDetailsPage.tabs.statsForm.europeanPlague")
            },
            {
                value: "americanPlague",
                desc: t("beehiveDetailsPage.tabs.statsForm.americanPlague")
            },
            {
                value: "nosema",
                desc: t("beehiveDetailsPage.tabs.statsForm.nosema")
            },
            {
                value: "other",
                desc: t("beehiveDetailsPage.tabs.statsForm.other")
            },
        ]

        const peculiaritiesStationInterferenceOptions: ICustomOption[] = [
            {
                value: "agriculturalPlantProtectionProductTreatments",
                desc: t("beehiveDetailsPage.tabs.statsForm.agriculturalPlantProtectionProductTreatments")
            },
            {
                value: "agriculturalProcessing",
                desc: t("beehiveDetailsPage.tabs.statsForm.agriculturalProcessing")
            },
            {
                value: "otherEvents",
                desc: t("beehiveDetailsPage.tabs.statsForm.otherEvents")
            },
        ]

        const marks = [
            {
                valueNumber: 0,
                label: t("beehiveDetailsPage.tabs.statsForm.poor"),
                value: "poor"
            },
            {
                valueNumber: 50,
                label: t("beehiveDetailsPage.tabs.statsForm.good"),
                value: "good"
            },
            {
                valueNumber: 100,
                label: t("beehiveDetailsPage.tabs.statsForm.excellent"),
                value: "excellent"
            },
        ];

        const varroaMarks = [
            {
                valueNumber: 0,
                label: t("beehiveDetailsPage.tabs.statsForm.notExisting"),
                value: "notExisting"
            },
            {
                valueNumber: 25,
                label: t("beehiveDetailsPage.tabs.statsForm.nothing"),
                value: "nothing"
            },
            {
                valueNumber: 50,
                label: t("beehiveDetailsPage.tabs.statsForm.low"),
                value: "low"
            },
            {
                valueNumber: 75,
                label: t("beehiveDetailsPage.tabs.statsForm.medium"),
                value: "medium"
            },
            {
                valueNumber: 100,
                label: t("beehiveDetailsPage.tabs.statsForm.high"),
                value: "high"
            },
        ];

        const [registrationDate, setRegistrationDate] = useState<Date | null>(null);
        const [dateSpringDeposition, setDateSpringDeposition] = useState<Date | null>(null);
        const [selectedPasture, setSelectedPasture] = useState<ICustomOption | undefined>(undefined);
        const [selectedPastureNote, setSelectedPastureNote] = useState<string | undefined>(undefined);
        const [selectedVarroaTreatment, setSelectedVarroaTreatment] = useState<ICustomOption | undefined>(undefined);
        const [selectedVarroaTreatmentNote, setSelectedVarroaTreatmentNote] = useState<string | undefined>(undefined);
        const [selectedOtherProblems, setSelectedOtherProblems] = useState<ICustomOption | undefined>(undefined);
        const [selectedOtherProblemsNote, setSelectedOtherProblemsNote] = useState<string | undefined>(undefined);
        const [selectedPeculiaritiesInference, setSelectedPeculiaritiesInference] = useState<ICustomOption | undefined>(undefined);
        const [selectedPeculiaritiesInferenceNote, setSelectedPeculiaritiesInferenceNote] = useState<string | undefined>(undefined);
        const [springDeposition, setSpringDeposition] = useState<string | null>(null);
        const [supplementalFeeding, setSupplementalFeeding] = useState<string | null>(null);
        const [plunder, setPlunder] = useState<string | null>(null);
        const [swarming, setSwarming] = useState<string | null>(null);
        const [queenLoss, setQueenLoss] = useState<string | null>(null);
        const [colonyStrength, setColonyStrength] = useState<number>(0);
        const [colonyStrengthValue, setColonyStrengthValue] = useState<string | undefined>(marks[0].value);
        const [broodQuality, setBroodQuality] = useState<number>(0);
        const [broodQualityValue, setBroodQualityValue] = useState<string | undefined>(marks[0].value);
        const [stockQuality, setStockQuality] = useState<number>(0);
        const [stockQualityValue, setStockQualityValue] = useState<string | undefined>(marks[0].value);
        const [varroaEstimation, setVarroaEstimation] = useState<number>(0);
        const [varroaEstimationValue, setVarroaEstimationValue] = useState<string | undefined>(varroaMarks[0].value);
        const [varroaFall, setVarroaFall] = useState<number>(0);
        const [varroaFallValue, setVarroaFallValue] = useState<string | undefined>(varroaMarks[0].value);

        useEffect(() => {
            if (currentBeehiveStat) {
                setSpringDeposition(currentBeehiveStat.startingSpringDeposition.isStarting)
                setDateSpringDeposition(new Date(currentBeehiveStat.startingSpringDeposition.date))
                setSupplementalFeeding(currentBeehiveStat.eatingType)
                setPlunder((currentBeehiveStat.looting as any).isLooting)
                setSwarming((currentBeehiveStat.swarming as any).isSwarming)
                setQueenLoss((currentBeehiveStat.orphanage as any).isOrphanaging)
                setRegistrationDate(new Date(currentBeehiveStat.timestamp))
                setSelectedPasture(pastureOptions.find(opt => opt.value === (currentBeehiveStat.mainFlower as any)?.flower))
                setSelectedPastureNote((currentBeehiveStat.mainFlower as any).otherValue || "")
                setSelectedVarroaTreatment(varroaTreatmentOptions.find(opt => opt.value === (currentBeehiveStat.varroaTreatment as any)?.type))
                setSelectedVarroaTreatmentNote((currentBeehiveStat.varroaTreatment as any).otherValue || "")
                setSelectedOtherProblems(otherSanitaryProblemsOptions.find(opt => opt.value === (currentBeehiveStat.otherSanitaryProblem as any)?.type))
                setSelectedOtherProblemsNote((currentBeehiveStat.otherSanitaryProblem as any).otherValue || "")
                setSelectedPeculiaritiesInference(peculiaritiesStationInterferenceOptions.find(opt => opt.value === (currentBeehiveStat.stationaryInterference as any)?.type))
                setSelectedPeculiaritiesInferenceNote((currentBeehiveStat.stationaryInterference as any)?.otherEventsValue || "")
                setColonyStrength(marks.find(mark => mark.value === currentBeehiveStat.colonyStrength)?.valueNumber || 0)
                setColonyStrengthValue(marks.find(mark => mark.value === currentBeehiveStat.colonyStrength)?.value)
                setBroodQuality(marks.find(mark => mark.value === currentBeehiveStat.broodQuality)?.valueNumber || 0)
                setBroodQualityValue(marks.find(mark => mark.value === currentBeehiveStat.broodQuality)?.value)
                setStockQuality(marks.find(mark => mark.value === currentBeehiveStat.stockQuality)?.valueNumber || 0)
                setStockQualityValue(marks.find(mark => mark.value === currentBeehiveStat.stockQuality)?.value)
                setVarroaEstimation(varroaMarks.find(mark => mark.value === currentBeehiveStat.varroaPresence)?.valueNumber || 0)
                setVarroaEstimationValue(varroaMarks.find(mark => mark.value === currentBeehiveStat.varroaPresence)?.value)
                setVarroaFallValue(varroaMarks.find(mark => mark.value === (currentBeehiveStat.varroaDeathAfterTreatment as any).type)?.value)
                setVarroaFall(varroaMarks.find(mark => mark.value === (currentBeehiveStat.varroaDeathAfterTreatment as any).type)?.valueNumber || 0)
                setVarroaFallValue(varroaMarks.find(mark => mark.value === (currentBeehiveStat.varroaDeathAfterTreatment as any).type)?.value)
            }
        }, [currentBeehiveStat])

        useEffect(() => {
            setBeehiveStatBody({
                timestamp: registrationDate && !isNaN(registrationDate.getTime()) ? registrationDate?.toISOString() : new Date().toISOString(),
                mainFlower: {
                    isFlowering: selectedPasture ? "yes" : "no",
                    flower: selectedPasture?.value,
                    date: registrationDate && !isNaN(registrationDate.getTime()) ? registrationDate?.toISOString() : new Date().toISOString(),
                    otherValue: selectedPastureNote
                },
                varroaTreatment: {
                    type: selectedVarroaTreatment?.value,
                    date: registrationDate && !isNaN(registrationDate.getTime()) ? registrationDate?.toISOString() : new Date().toISOString(),
                    otherValue: selectedVarroaTreatmentNote
                },
                otherSanitaryProblem: {
                    type: selectedOtherProblems?.value,
                    date: registrationDate && !isNaN(registrationDate.getTime()) ? registrationDate?.toISOString() : new Date().toISOString(),
                    otherValue: selectedOtherProblemsNote
                },
                stationaryInterference: {
                    type: selectedPeculiaritiesInference?.value,
                    date: registrationDate && !isNaN(registrationDate.getTime()) ? registrationDate?.toISOString() : new Date().toISOString(),
                    otherEventsValue: selectedPeculiaritiesInferenceNote
                },
                startingSpringDeposition: {
                    isStarting: springDeposition ? springDeposition : "no",
                    date: dateSpringDeposition && !isNaN(dateSpringDeposition.getTime()) ? dateSpringDeposition?.toISOString() : new Date().toISOString(),
                },
                eatingType: supplementalFeeding ? supplementalFeeding : "solid",
                looting: {
                    date: registrationDate && !isNaN(registrationDate.getTime()) ? registrationDate?.toISOString() : new Date().toISOString(),
                    isLooting: plunder
                },
                swarming: {
                    date: registrationDate && !isNaN(registrationDate.getTime()) ? registrationDate?.toISOString() : new Date().toISOString(),
                    isSwarming: swarming
                },
                orphanage: {
                    isOrphanaging: queenLoss,
                    date: registrationDate && !isNaN(registrationDate.getTime()) ? registrationDate?.toISOString() : new Date().toISOString(),
                },
                colonyStrength: colonyStrengthValue || "",
                broodQuality: broodQualityValue || "",
                stockQuality: stockQualityValue || "",
                varroaPresence: varroaEstimationValue || "",
                varroaDeathAfterTreatment: {
                    type: varroaFallValue,
                    date: registrationDate && !isNaN(registrationDate.getTime()) ? registrationDate?.toISOString() : new Date().toISOString(),
                }
            })
        }, [
            registrationDate,
            selectedPasture,
            selectedVarroaTreatment,
            selectedOtherProblems,
            selectedPeculiaritiesInference,
            springDeposition,
            supplementalFeeding,
            plunder,
            swarming,
            queenLoss,
            colonyStrengthValue,
            broodQualityValue,
            stockQualityValue,
            varroaEstimationValue,
            varroaFallValue,
            selectedOtherProblemsNote,
            selectedPeculiaritiesInferenceNote,
            selectedVarroaTreatmentNote,
            selectedPastureNote,
            dateSpringDeposition
        ])

        return <Box sx={{ display: "flex", flexDirection: "column", px: largeMediumScreen ? 2 : 0 }}>
            <Typography id="registration-date-typo" variant="h6" sx={{ fontWeight: 500, mb: 2 }} gutterBottom>
                {t("beehiveDetailsPage.tabs.statsForm.registrationDate")}
            </Typography>
            <Box sx={{ mb: 2, px: 1 }}>
                <DatePicker
                    slotProps={{ textField: { size: 'small' } }}
                    label={t("beehiveDetailsPage.tabs.statsForm.registrationDate")}
                    sx={{ mr: 2 }}
                    value={registrationDate}
                    onChange={(value) => setRegistrationDate(value)}
                />
            </Box>
            <Divider />
            <Typography variant="h6" sx={{ my: 1 }}>
                {t("beehiveDetailsPage.tabs.statsForm.beehive")}
            </Typography>
            <Box sx={{ px: 1, display: "flex", flexDirection: "column" }}>
                <FormControl sx={{ mb: 2 }} required>
                    <Typography id="spring-deposition" sx={{ fontWeight: 500 }} gutterBottom>
                        {t("beehiveDetailsPage.tabs.statsForm.beginningSpringDeposition")}
                    </Typography>
                    <RadioGroup
                        row
                        aria-labelledby="spring-deposition"
                        name="spring-deposition"
                        onChange={(event) => setSpringDeposition(event.target.value)}
                        value={springDeposition}
                    >
                        <FormControlLabel value="yes" control={<Radio />} label={t("beehiveDetailsPage.tabs.statsForm.yes")} />
                        <FormControlLabel value="no" control={<Radio />} label={t("beehiveDetailsPage.tabs.statsForm.no")} />
                    </RadioGroup>
                    {
                        springDeposition === 'yes' && <Box sx={{ my: 2 }}>
                            <DatePicker
                                slotProps={{ textField: { size: 'small' } }}
                                label={t("beehiveDetailsPage.tabs.statsForm.dateSpringDeposition")}
                                sx={{ mr: 2 }}
                                value={dateSpringDeposition}
                                onChange={(value) => setDateSpringDeposition(value)}
                            />
                        </Box>
                    }
                </FormControl>
                <Box sx={{ mb: 2 }}>
                    <Typography id="colony-strength-label" sx={{ fontWeight: 500 }} gutterBottom>
                        {t("beehiveDetailsPage.tabs.statsForm.colonyStrength")}
                    </Typography>
                    <Box sx={{ width: "90%", margin: "0 auto" }}>
                        <Slider
                            aria-label="Colony strength"
                            aria-labelledby="colony-strength-label"
                            defaultValue={0}
                            step={null}
                            valueLabelDisplay="off"
                            marks={marks.map(mark => { return { value: mark.valueNumber, label: mark.label } })}
                            value={colonyStrength}
                            onChange={(_, value) => {
                                if (typeof value === "number") {
                                    setColonyStrength(value)
                                    setColonyStrengthValue(marks.find(mark => mark.valueNumber === value)?.value)
                                }
                            }}
                        />
                    </Box>
                </Box>
                <Box sx={{ mb: 2 }}>
                    <Typography id="brood-quality-label" sx={{ fontWeight: 500 }} gutterBottom>
                        {t("beehiveDetailsPage.tabs.statsForm.broodQuality")}
                    </Typography>
                    <Box sx={{ width: "90%", margin: "0 auto" }}>
                        <Slider
                            aria-label="Brood quality"
                            aria-labelledby="brood-quality-label"
                            defaultValue={0}
                            step={null}
                            valueLabelDisplay="off"
                            marks={marks.map(mark => { return { value: mark.valueNumber, label: mark.label } })}
                            value={broodQuality}
                            onChange={(_, value) => {
                                if (typeof value === "number") {
                                    setBroodQuality(value)
                                    setBroodQualityValue(marks.find(mark => mark.valueNumber === value)?.value)
                                }
                            }}
                        />
                    </Box>
                </Box>
                <Box sx={{ mb: 2 }}>
                    <Typography id="stock-quality-label" sx={{ fontWeight: 500 }} gutterBottom>
                        {t("beehiveDetailsPage.tabs.statsForm.stockQuality")}
                    </Typography>
                    <Box sx={{ width: "90%", margin: "0 auto" }}>
                        <Slider
                            aria-label="Stock quality"
                            aria-labelledby="stock-quality-label"
                            defaultValue={0}
                            step={null}
                            valueLabelDisplay="off"
                            marks={marks.map(mark => { return { value: mark.valueNumber, label: mark.label } })}
                            value={stockQuality}
                            onChange={(_, value) => {
                                if (typeof value === "number") {
                                    setStockQuality(value)
                                    setStockQualityValue(marks.find(mark => mark.valueNumber === value)?.value)
                                }
                            }}
                        />
                    </Box>
                </Box>
                <FormControl sx={{ mb: 2 }}>
                    <Typography id="supplemental-feeding" sx={{ fontWeight: 500 }} gutterBottom>
                        {t("beehiveDetailsPage.tabs.statsForm.supplementalFeeding")}
                    </Typography>
                    <RadioGroup
                        row
                        aria-labelledby="supplemental-feeding"
                        name="supplemental-feeding"
                        onChange={(event) => setSupplementalFeeding(event.target.value)}
                        value={supplementalFeeding}
                    >
                        <FormControlLabel value="solid" control={<Radio />} label={t("beehiveDetailsPage.tabs.statsForm.solid")} />
                        <FormControlLabel value="liquid" control={<Radio />} label={t("beehiveDetailsPage.tabs.statsForm.liquid")} />
                        <FormControlLabel value="notRequired" control={<Radio />} label={t("beehiveDetailsPage.tabs.statsForm.notRequired")} />
                    </RadioGroup>
                </FormControl>
                <FormControl sx={{ mb: 2 }}>
                    <Typography id="plunder" sx={{ fontWeight: 500 }} gutterBottom>
                        {t("beehiveDetailsPage.tabs.statsForm.plunder")}
                    </Typography>
                    <RadioGroup
                        row
                        aria-labelledby="plunder"
                        name="plunder"
                        onChange={(event) => setPlunder(event.target.value)}
                        value={plunder}
                    >
                        <FormControlLabel value="yes" control={<Radio />} label={t("beehiveDetailsPage.tabs.statsForm.yes")} />
                        <FormControlLabel value="no" control={<Radio />} label={t("beehiveDetailsPage.tabs.statsForm.no")} />
                    </RadioGroup>
                </FormControl>
                <FormControl sx={{ mb: 2 }}>
                    <Typography id="swarming" sx={{ fontWeight: 500 }} gutterBottom>
                        {t("beehiveDetailsPage.tabs.statsForm.swarming")}
                    </Typography>
                    <RadioGroup
                        row
                        aria-labelledby="swarming"
                        name="swarming"
                        onChange={(event) => setSwarming(event.target.value)}
                        value={swarming}
                    >
                        <FormControlLabel value="yes" control={<Radio />} label={t("beehiveDetailsPage.tabs.statsForm.yes")} />
                        <FormControlLabel value="no" control={<Radio />} label={t("beehiveDetailsPage.tabs.statsForm.no")} />
                    </RadioGroup>
                </FormControl>
                <FormControl sx={{ mb: 2 }}>
                    <Typography id="queen-loss" sx={{ fontWeight: 500 }} gutterBottom>
                        {t("beehiveDetailsPage.tabs.statsForm.queenLoss")}
                    </Typography>
                    <RadioGroup
                        row
                        aria-labelledby="queen-loss"
                        name="queen-loss"
                        onChange={(event) => setQueenLoss(event.target.value)}
                        value={queenLoss}
                    >
                        <FormControlLabel value="yes" control={<Radio />} label={t("beehiveDetailsPage.tabs.statsForm.yes")} />
                        <FormControlLabel value="no" control={<Radio />} label={t("beehiveDetailsPage.tabs.statsForm.no")} />
                    </RadioGroup>
                </FormControl>
            </Box>
            <Divider />
            <Typography variant="h6" sx={{ my: 1 }}>
                {t("beehiveDetailsPage.tabs.statsForm.environment")}
            </Typography>
            <Box sx={{ px: 1 }}>
                <Typography id="main-pasture-typo" sx={{ fontWeight: 500, mb: 2 }} gutterBottom>
                    {t("beehiveDetailsPage.tabs.statsForm.mainPasture")}
                </Typography>
                <FormControl size="small" sx={{ minWidth: largeMediumScreen ? 300 : "100%", mb: 2 }}>
                    <InputLabel id="main-pasture-select-label">
                        {t("beehiveDetailsPage.tabs.statsForm.mainPasture")}
                    </InputLabel>
                    <Select
                        id="main-pasture-select"
                        value={selectedPasture?.value.toString() || ""}
                        name="main-pasture"
                        onChange={(event) => {
                            setSelectedPasture(pastureOptions.find(opt => opt.value === event.target.value))
                            if (event.target.value !== 'other') {
                                setSelectedPastureNote(undefined)
                            }
                        }}
                        labelId="main-pasture-select-label"
                        label={t("beehiveDetailsPage.tabs.statsForm.mainPasture")}
                        MenuProps={{
                            style: {
                                maxHeight: 400,
                            },
                        }}
                    >
                        <MenuItem value="">
                            <em>{t("beehiveDetailsPage.tabs.statsForm.genericSelectLabel")}</em>
                        </MenuItem>
                        {
                            pastureOptions && pastureOptions.map(
                                (option) => (
                                    <MenuItem
                                        key={option.value + "-" + option.desc}
                                        value={option.value}
                                    >
                                        {option.desc}
                                    </MenuItem>
                                )
                            )
                        }
                    </Select>
                    {
                        selectedPasture && selectedPasture.value === "other" && <TextField
                            label={t("beehiveDetailsPage.tabs.statsForm.note")}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => setSelectedPastureNote(e.target.value)}
                            defaultValue={selectedPastureNote}
                            size="small"
                            multiline
                            sx={{ mt: 3 }}
                        />
                    }
                </FormControl>
            </Box>
            <Box sx={{ px: 1 }}>
                <Typography id="peculiarities-station-interference-typo" sx={{ fontWeight: 500, mb: 2 }} gutterBottom>
                    {t("beehiveDetailsPage.tabs.statsForm.peculiaritiesStationInterference")}
                </Typography>
                <FormControl size="small" sx={{ minWidth: largeMediumScreen ? 300 : "100%", mb: 2 }}>
                    <InputLabel id="peculiarities-station-interference-select-label">
                        {t("beehiveDetailsPage.tabs.statsForm.peculiaritiesStationInterference")}
                    </InputLabel>
                    <Select
                        id="peculiarities-station-interference-select"
                        value={selectedPeculiaritiesInference?.value.toString() || ""}
                        name="peculiarities-station-interference"
                        onChange={(event) => setSelectedPeculiaritiesInference(peculiaritiesStationInterferenceOptions.find(opt => opt.value === event.target.value))}
                        labelId="peculiarities-station-interference-select-label"
                        label={t("beehiveDetailsPage.tabs.statsForm.peculiaritiesStationInterference")}
                        MenuProps={{
                            style: {
                                maxHeight: 400,
                            },
                        }}
                    >
                        <MenuItem value="">
                            <em>{t("beehiveDetailsPage.tabs.statsForm.genericSelectLabel")}</em>
                        </MenuItem>
                        {
                            peculiaritiesStationInterferenceOptions && peculiaritiesStationInterferenceOptions.map(
                                (option) => (
                                    <MenuItem
                                        key={option.value + "-" + option.desc}
                                        value={option.value}
                                    >
                                        {option.desc}
                                    </MenuItem>
                                )
                            )
                        }
                    </Select>
                    {
                        selectedPeculiaritiesInference && selectedPeculiaritiesInference.value === "otherEvents" && <TextField
                            label={t("beehiveDetailsPage.tabs.statsForm.note")}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => setSelectedPeculiaritiesInferenceNote(e.target.value)}
                            defaultValue={selectedPeculiaritiesInferenceNote}
                            size="small"
                            multiline
                            sx={{ mt: 3 }}
                        />
                    }
                </FormControl>
            </Box>
            <Divider />
            <Typography variant="h6" sx={{ my: 1 }}>
                {t("beehiveDetailsPage.tabs.statsForm.sanitaryAspects")}
            </Typography>
            <Box sx={{ px: 1 }}>
                <Box sx={{ mb: 2 }}>
                    <Typography id="varroa-mite-estimation-level-label" sx={{ fontWeight: 500 }} gutterBottom>
                        {t("beehiveDetailsPage.tabs.statsForm.varroaMiteEstimationLevel")}
                    </Typography>
                    <Box sx={{ width: "90%", margin: "0 auto" }}>
                        <Slider
                            aria-label="Varroa mite estimation level"
                            aria-labelledby="varroa-mite-estimation-level-label"
                            defaultValue={0}
                            step={null}
                            valueLabelDisplay="off"
                            marks={varroaMarks.map(mark => { return { value: mark.valueNumber, label: mark.label } })}
                            value={varroaEstimation}
                            onChange={(_, value) => {
                                if (typeof value === "number") {
                                    setVarroaEstimation(value)
                                    setVarroaEstimationValue(varroaMarks.find(mark => mark.valueNumber === value)?.value)
                                }
                            }}
                        />
                    </Box>
                </Box>
                <Box sx={{ mb: 2 }}>
                    <Typography id="varroa-control-treatment-typo" sx={{ fontWeight: 500, mb: 2 }} gutterBottom>
                        {t("beehiveDetailsPage.tabs.statsForm.varroaControlTreatment")}
                    </Typography>
                    <FormControl size="small" sx={{ minWidth: largeMediumScreen ? 300 : "100%", mb: 2 }}>
                        <InputLabel id="varroa-control-treatment-select-label">
                            {t("beehiveDetailsPage.tabs.statsForm.varroaControlTreatment")}
                        </InputLabel>
                        <Select
                            id="varroa-control-treatment-select"
                            value={selectedVarroaTreatment?.value.toString() || ""}
                            name="varroa-control-treatment"
                            onChange={(event) => {
                                setSelectedVarroaTreatment(varroaTreatmentOptions.find(opt => opt.value === event.target.value))
                                if (event.target.value !== 'other') {
                                    setSelectedVarroaTreatmentNote(undefined)
                                }
                            }}
                            labelId="varroa-control-treatment-select-label"
                            label={t("beehiveDetailsPage.tabs.statsForm.varroaControlTreatment")}
                            MenuProps={{
                                style: {
                                    maxHeight: 400,
                                },
                            }}
                        >
                            <MenuItem value="">
                                <em>{t("beehiveDetailsPage.tabs.statsForm.genericSelectLabel")}</em>
                            </MenuItem>
                            {
                                varroaTreatmentOptions && varroaTreatmentOptions.map(
                                    (option) => (
                                        <MenuItem
                                            key={option.value + "-" + option.desc}
                                            value={option.value}
                                        >
                                            {option.desc}
                                        </MenuItem>
                                    )
                                )
                            }
                        </Select>
                        {
                            selectedVarroaTreatment && selectedVarroaTreatment.value === "other" && <TextField
                                label={t("beehiveDetailsPage.tabs.statsForm.note")}
                                onChange={(e: ChangeEvent<HTMLInputElement>) => setSelectedVarroaTreatmentNote(e.target.value)}
                                defaultValue={selectedVarroaTreatmentNote}
                                size="small"
                                multiline
                                sx={{ mt: 3 }}
                            />
                        }
                    </FormControl>
                </Box>
                <Box sx={{ mb: 2 }}>
                    <Typography id="varroa-mite-fall-count-after-treatment-label" sx={{ fontWeight: 500 }} gutterBottom>
                        {t("beehiveDetailsPage.tabs.statsForm.varroaMiteFallCountAfterTreatment")}
                    </Typography>
                    <Box sx={{ width: "90%", margin: "0 auto" }}>
                        <Slider
                            aria-label="Varroa mite fall count after treatment"
                            aria-labelledby="varroa-mite-fall-count-after-treatment-label"
                            defaultValue={0}
                            step={null}
                            valueLabelDisplay="off"
                            marks={varroaMarks.map(mark => { return { value: mark.valueNumber, label: mark.label } })}
                            value={varroaFall}
                            onChange={(_, value) => {
                                if (typeof value === "number") {
                                    setVarroaFall(value)
                                    setVarroaFallValue(varroaMarks.find(mark => mark.valueNumber === value)?.value)
                                }
                            }}
                        />
                    </Box>
                </Box>
                <Box sx={{ mb: 2 }}>
                    <Typography id="other-sanitary-issues-typo" sx={{ fontWeight: 500, mb: 2 }} gutterBottom>
                        {t("beehiveDetailsPage.tabs.statsForm.otherSanitaryIssues")}
                    </Typography>
                    <FormControl size="small" sx={{ minWidth: largeMediumScreen ? 300 : "100%", mb: 2 }}>
                        <InputLabel id="other-sanitary-issues-select-label">
                            {t("beehiveDetailsPage.tabs.statsForm.otherSanitaryIssues")}
                        </InputLabel>
                        <Select
                            id="other-sanitary-issues-select"
                            value={selectedOtherProblems?.value.toString() || ""}
                            name="other-sanitary-issues"
                            onChange={(event) => {
                                setSelectedOtherProblems(otherSanitaryProblemsOptions.find(opt => opt.value === event.target.value))
                                if (event.target.value !== 'other') {
                                    setSelectedOtherProblemsNote(undefined)
                                }
                            }}
                            labelId="other-sanitary-issues-select-label"
                            label={t("beehiveDetailsPage.tabs.statsForm.otherSanitaryIssues")}
                            MenuProps={{
                                style: {
                                    maxHeight: 400,
                                },
                            }}
                        >
                            <MenuItem value="">
                                <em>{t("beehiveDetailsPage.tabs.statsForm.genericSelectLabel")}</em>
                            </MenuItem>
                            {
                                otherSanitaryProblemsOptions && otherSanitaryProblemsOptions.map(
                                    (option) => (
                                        <MenuItem
                                            key={option.value + "-" + option.desc}
                                            value={option.value}
                                        >
                                            {option.desc}
                                        </MenuItem>
                                    )
                                )
                            }
                        </Select>
                        {
                            selectedOtherProblems && selectedOtherProblems.value === "other" && <TextField
                                label={t("beehiveDetailsPage.tabs.statsForm.note")}
                                onChange={(e: ChangeEvent<HTMLInputElement>) => setSelectedOtherProblemsNote(e.target.value)}
                                defaultValue={selectedOtherProblemsNote}
                                size="small"
                                multiline
                                sx={{ mt: 3 }}
                            />
                        }
                    </FormControl>
                </Box>
            </Box>
        </Box>
    }

export default DialogStat;